
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Profile {
  id: string;
  email?: string;
  full_name?: string;
  role: string;
  phone?: string;
  national_id?: string;
  language?: string;
  kyc_status?: string;
  id_front_url?: string;
  id_back_url?: string;
  avatar_url?: string;
  address_country?: string;
  address_city?: string;
  address_area?: string;
  address_street?: string;
  address_building?: string;
  address_apt?: string;
  expected_move_in?: string;
  budget_max?: number;
  roommates_count?: number;
  gender?: string;
  property_ownership_type?: string;
  payout_method?: string;
  university_id?: string;
  whatsapp_contact_count?: number;
  created_at?: string;
  updated_at?: string;
}

interface SignUpData {
  full_name?: string;
  role?: string;
  gender?: string;
  university_id?: string | null;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  signUp: (email: string, password: string, data?: SignUpData) => Promise<{ error?: any }>;
  signIn: (email: string, password: string) => Promise<{ error?: any }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error?: any }>;
  resendConfirmation: (email: string) => Promise<{ error?: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to get user-friendly error messages
const getErrorMessage = (error: any, type: 'signin' | 'signup') => {
  if (!error?.message) return 'An unknown error occurred';
  
  const message = error.message.toLowerCase();
  
  if (type === 'signin') {
    if (message.includes('invalid login credentials') || message.includes('invalid credentials')) {
      return 'The email or password you entered is incorrect. Please check your credentials and try again.';
    }
    if (message.includes('email not confirmed') || message.includes('email_not_confirmed')) {
      return 'Your account is not verified yet. Please check your email and click the verification link before signing in.';
    }
    if (message.includes('user not found')) {
      return 'No account found with this email address. Please check your email or sign up for a new account.';
    }
    if (message.includes('signup is disabled')) {
      return 'Your account has been disabled. Please contact support for assistance.';
    }
    if (message.includes('too many requests')) {
      return 'Too many sign-in attempts. Please wait a few minutes before trying again.';
    }
    if (message.includes('network')) {
      return 'Network error. Please check your internet connection and try again.';
    }
  }
  
  if (type === 'signup') {
    if (message.includes('user already registered')) {
      return 'An account with this email already exists. Please try signing in instead.';
    }
    if (message.includes('password should be at least')) {
      return 'Password must be at least 6 characters long.';
    }
    if (message.includes('invalid email')) {
      return 'Please enter a valid email address.';
    }
    if (message.includes('weak password')) {
      return 'Password is too weak. Please use a combination of letters, numbers, and symbols.';
    }
    if (message.includes('signup is disabled')) {
      return 'Account registration is currently disabled. Please contact support.';
    }
  }
  
  // Return original message if we can't map it to a user-friendly one
  return error.message;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth event:', event, 'Session exists:', !!session);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          fetchProfile(session.user.id);
        } else {
          setProfile(null);
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial session:', !!session);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      console.log('Fetching profile for:', userId);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      console.log('Profile fetch result:', { data: !!data, error });
      
      if (error) {
        console.error('Profile fetch error:', error);
        // Try admin function as fallback
        const { data: adminData, error: adminError } = await supabase
          .rpc('get_profile_for_kyc_admin_secure', { target_user_id: userId });
        
        if (!adminError && adminData && adminData.length > 0) {
          console.log('Got profile via admin function');
          setProfile(adminData[0]);
          return;
        }
      }

      if (data) {
        console.log('Profile loaded:', data.full_name, data.role);
        setProfile(data);
      } else {
        console.log('No profile found');
        setProfile(null);
      }
    } catch (error) {
      console.error('Profile fetch exception:', error);
      setProfile(null);
    }
  };

  const signUp = async (email: string, password: string, data: SignUpData = {}) => {
    console.log('📝 Starting signup process for:', email);
    
    try {
      // Enhanced password validation
      if (password.length < 8) {
        const error = { message: 'Password must be at least 8 characters long' };
        toast.error(error.message);
        return { error };
      }

      // Check for common weak passwords
      const weakPasswords = ['password', '12345678', 'qwerty123'];
      if (weakPasswords.includes(password.toLowerCase())) {
        const error = { message: 'Please choose a stronger password' };
        toast.error(error.message);
        return { error };
      }
      
      const { data: authData, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/email-verification`,
          data: {
            full_name: data.full_name || '',
            role: data.role || 'user'
          }
        }
      });

      if (error) {
        console.error('❌ Signup error:', error);
        const userFriendlyMessage = getErrorMessage(error, 'signup');
        toast.error(userFriendlyMessage);
        return { error };
      }

      // If user is created, update their profile with additional data
      if (authData.user) {
        try {
          const { error: profileError } = await supabase
            .from('profiles')
            .update({
              gender: data.gender,
              university_id: data.university_id,
              role: data.role || 'user',
              whatsapp_contact_count: 0
            })
            .eq('id', authData.user.id);

          if (profileError) {
            console.error('❌ Profile update error:', profileError);
          }
        } catch (profileError) {
          console.error('❌ Profile update exception:', profileError);
        }
      }

      console.log('✅ Signup successful, user created:', authData.user?.id);

      if (authData.user && !authData.user.email_confirmed_at) {
        toast.success('🎉 Account created successfully! Please check your email and click the verification link to activate your account.', {
          duration: 8000,
          description: 'Make sure to check your spam folder if you don\'t see the email within a few minutes.'
        });
      } else {
        toast.success('Account created and verified successfully!');
      }

      return { error: null };
    } catch (error) {
      console.error('❌ Signup exception:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Signup failed: ${errorMessage}`);
      return { error: errorMessage };
    }
  };

  const resendConfirmation = async (email: string) => {
    console.log('📧 Resending confirmation email for:', email);
    
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/email-verification`
        }
      });

      if (error) {
        console.error('❌ Resend confirmation error:', error);
        const userFriendlyMessage = getErrorMessage(error, 'signup');
        toast.error(userFriendlyMessage);
      } else {
        console.log('✅ Confirmation email resent successfully');
        toast.success('Confirmation email resent! Please check your inbox.');
      }

      return { error };
    } catch (error) {
      console.error('❌ Resend confirmation exception:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Failed to resend confirmation: ${errorMessage}`);
      return { error: errorMessage };
    }
  };

  const signIn = async (email: string, password: string) => {
    console.log('🔑 Starting signin process for:', email);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('❌ Signin error:', error.message, error);
        const userFriendlyMessage = getErrorMessage(error, 'signin');
        
        // Show more detailed error messages based on error type
        if (error.message.toLowerCase().includes('email not confirmed') || error.message.toLowerCase().includes('email_not_confirmed')) {
          toast.error('⚠️ Account Not Verified', {
            description: 'Please check your email and click the verification link before signing in. Check spam folder if needed.',
            duration: 8000,
            action: {
              label: 'Resend Email',
              onClick: () => resendConfirmation(email)
            }
          });
        } else if (error.message.toLowerCase().includes('invalid login credentials') || error.message.toLowerCase().includes('invalid credentials')) {
          toast.error('🔒 Sign In Failed', {
            description: 'The email or password you entered is incorrect. Please double-check your credentials.',
            duration: 6000
          });
        } else {
          toast.error('❌ Sign In Error', {
            description: userFriendlyMessage,
            duration: 6000
          });
        }
      } else {
        console.log('✅ Signin successful');
        toast.success('🎉 Welcome back!', {
          description: 'You have successfully signed in to your account.'
        });
      }

      return { error };
    } catch (error) {
      console.error('❌ Signin exception:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Sign in failed: ${errorMessage}`);
      return { error: errorMessage };
    }
  };

  const signOut = async () => {
    console.log('🚪 Signing out...');
    
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('❌ Signout error:', error);
        toast.error(error.message);
      } else {
        console.log('✅ Signed out successfully');
        setUser(null);
        setProfile(null);
        setSession(null);
        toast.success('Signed out successfully');
      }
    } catch (error) {
      console.error('❌ Signout exception:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Sign out failed: ${errorMessage}`);
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) {
      console.error('❌ No user logged in');
      toast.error('You must be logged in to update your profile');
      return { error: 'No user logged in' };
    }

    console.log('📝 Updating profile for user:', user.id);
    console.log('Raw update data:', updates);

    try {
      // Clean undefined values and ensure proper types
      const cleanUpdates: Record<string, any> = {};
      
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          // Handle UUID conversion for university_id
          if (key === 'university_id' && value) {
            cleanUpdates[key] = String(value);
          } else {
            cleanUpdates[key] = value;
          }
        }
      });
      
      console.log('Cleaned update data:', cleanUpdates);
      
      // First try a direct update to see if RLS is working
      console.log('🔄 Attempting direct profile update...');
      const { data: directUpdate, error: directError } = await supabase
        .from('profiles')
        .update(cleanUpdates)
        .eq('id', user.id)
        .select('*')
        .single();
      
      if (directError) {
        console.error('❌ Direct update failed:', directError);
        
        // Fallback to RPC function
        console.log('🔄 Trying RPC function...');
        const { data: rpcData, error: rpcError } = await supabase.rpc('secure_profile_update', {
          update_data: cleanUpdates
        });

        console.log('RPC response - data:', rpcData, 'error:', rpcError);

        if (rpcError) {
          console.error('❌ RPC update also failed:', rpcError);
          toast.error(`Failed to update profile: ${rpcError.message}`);
          return { error: rpcError.message };
        }

        if (!rpcData) {
          console.error('❌ RPC returned false');
          toast.error('Profile update failed - please try again');
          return { error: 'Update operation failed' };
        }
      } else {
        console.log('✅ Direct update successful:', directUpdate);
      }

      console.log('✅ Profile updated successfully');
      
      // Refresh the profile data from database to get the updated values
      console.log('🔄 Refreshing profile data...');
      const { data: updatedProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (fetchError) {
        console.error('❌ Error fetching updated profile:', fetchError);
        // Still update local state with provided updates as fallback
        setProfile(prev => prev ? { ...prev, ...cleanUpdates } : null);
      } else {
        console.log('✅ Profile refreshed from database:', updatedProfile);
        setProfile(updatedProfile);
      }

      console.log('🎉 Showing success notification');
      toast.success('Profile updated successfully!', {
        description: 'Your changes have been saved.',
        duration: 3000
      });
      
      return { error: null };
    } catch (exception) {
      console.error('❌ Profile update exception:', exception);
      const errorMessage = exception instanceof Error ? exception.message : 'Unknown error occurred';
      toast.error(`Failed to update profile: ${errorMessage}`, {
        description: 'Please try again or contact support if the issue persists.',
        duration: 5000
      });
      return { error: errorMessage };
    }
  };

  const value = {
    user,
    profile,
    session,
    signUp,
    signIn,
    signOut,
    updateProfile,
    resendConfirmation
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
